/**
 * PaymentPortal Component
 * 
 * Main container for payment method tokenization workflow.
 * Manages state transitions and orchestrates payment method vaulting flow.
 * 
 * Task: T058
 * 
 * Props:
 * - email: User email (from URL or manual entry)
 * - requestId: Payment request ID (from URL or manual entry)
 * - onError: Callback for errors
 * - onBack: Callback to return to manual entry
 * 
 * State Flow:
 * 1. VALIDATING: Validate credentials
 * 2. FETCHING_TOKEN: Get Braintree client token
 * 3. PAYMENT_FORM: Show Drop-in UI
 * 4. SUBMITTING: Submit payment method for vaulting
 * 5. SUCCESS: Show success page
 */

import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Button } from '@material-tailwind/react';
import {
  validatePaymentRequest,
  getClientToken,
  submitPaymentMethod,
  getErrorMessage,
  getCorrelationId,
} from '../services/api';
import BraintreeDropIn from './BraintreeDropIn';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import SuccessPage from '../pages/SuccessPage';

// Workflow states
const STATES = {
  VALIDATING: 'validating',
  FETCHING_TOKEN: 'fetching_token',
  PAYMENT_FORM: 'payment_form',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error',
};

const PaymentPortal = ({ email, requestId, onError, onBack }) => {
  const [currentState, setCurrentState] = useState(STATES.VALIDATING);
  const [error, setError] = useState(null);
  const [correlationId, setCorrelationId] = useState(null);
  const [clientToken, setClientToken] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [validationChecked, setValidationChecked] = useState(false);
  const [requestType, setRequestType] = useState(null);
  const [customerName, setCustomerName] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [caseNumber, setCaseNumber] = useState(null);

  // T087: Check if payment already completed on mount (page refresh after success)
  useEffect(() => {
    if (!requestId) return;

    const checkCompletionStatus = async () => {
      try {
        // Attempt validation - if request is completed, backend returns 404
        const response = await validatePaymentRequest({
          email,
          requestId,
        });

        if (response.valid) {
          // Request is still pending - proceed normally
          setValidationChecked(false);
        }
      } catch (err) {
        // If 404, could be expired, completed, or not found
        // Check if it's a completion scenario
        if (err.response && err.response.status === 404) {
          const errorMessage = getErrorMessage(err);
          if (errorMessage.includes('completed')) {
            // Request was already completed - show success
            setPaymentResult({
              paymentMethodId: null,
              requestId: requestId,
              timestamp: new Date().toISOString(),
            });
            setCurrentState(STATES.SUCCESS);
          }
        }
      }
    };

    checkCompletionStatus();
  }, [requestId, email]);

  // Step 1: Validate payment request credentials
  useEffect(() => {
    if (validationChecked) return;

    const validate = async () => {
      try {
        setCurrentState(STATES.VALIDATING);
        setError(null);

        console.log('[PaymentPortal] Validating credentials...');

        const response = await validatePaymentRequest({
          email,
          requestId,
        });

        if (!response.valid) {
          throw new Error(response.message || 'Invalid payment request');
        }

        console.log('[PaymentPortal] Validation successful');
        
        // Store request type
        setRequestType(response.type);
        
        // Store type-specific data
        if (response.type === 'PaymentMethod') {
          setCustomerName(response.customerName);
        } else if (response.type === 'Payment') {
          setCustomerName(response.customerName);
          setPaymentData({
            total: response.total,
            billTo: response.billTo,
            shipTo: response.shipTo,
            lineItems: response.lineItems
          });
        } else if (response.type === 'Salesforce') {
          setCaseNumber(response.caseNumber);
        }
        
        setValidationChecked(true);
        setCurrentState(STATES.FETCHING_TOKEN);
      } catch (err) {
        console.error('[PaymentPortal] Validation error:', err);
        const errorMessage = getErrorMessage(err);
        const errorCorrelationId = getCorrelationId(err);
        
        // Check if request not found (404)
        // Backend returns 404 for: not found, email mismatch, or already completed
        if (err.response && err.response.status === 404) {
          setError('Payment method request not found. Please check your email and request code are correct.');
        } else {
          setError(errorMessage);
        }
        
        setCorrelationId(errorCorrelationId);
        setCurrentState(STATES.ERROR);
        setValidationChecked(true); // Prevent retry loop
        onError?.(err);
      }
    };

    validate();
  }, [email, requestId, validationChecked, onError]);

  // Step 2: Fetch Braintree client token
  useEffect(() => {
    if (currentState !== STATES.FETCHING_TOKEN) return;

    const fetchToken = async () => {
      try {
        console.log('[PaymentPortal] Fetching client token...');

        const response = await getClientToken(requestId);

        if (!response.clientToken) {
          throw new Error('No client token received');
        }

        console.log('[PaymentPortal] Client token received');
        setClientToken(response.clientToken);
        setCurrentState(STATES.PAYMENT_FORM);
      } catch (err) {
        console.error('[PaymentPortal] Token fetch error:', err);
        const errorMessage = getErrorMessage(err);
        const errorCorrelationId = getCorrelationId(err);
        
        setError(errorMessage);
        setCorrelationId(errorCorrelationId);
        setCurrentState(STATES.ERROR);
        onError?.(err);
      }
    };

    fetchToken();
  }, [currentState, requestId, onError]);

  // Step 3: Handle payment method submission
  const handlePaymentMethodReceived = async (payload) => {
    try {
      setCurrentState(STATES.SUBMITTING);
      setError(null);

      console.log('[PaymentPortal] Submitting payment method...');

      const response = await submitPaymentMethod({
        requestId,
        nonce: payload.nonce,
      });

      console.log('[PaymentPortal] Payment successful:', response);

      setPaymentResult({
        paymentMethodId: response.paymentMethodId,
        requestId: response.requestId,
        timestamp: new Date().toISOString(),
      });
      setCurrentState(STATES.SUCCESS);
    } catch (err) {
      console.error('[PaymentPortal] Payment submission error:', err);
      const errorMessage = getErrorMessage(err);
      const errorCorrelationId = getCorrelationId(err);
      
      setError(errorMessage);
      setCorrelationId(errorCorrelationId);
      setCurrentState(STATES.ERROR);
      onError?.(err);
    }
  };

  // Handle Drop-in errors
  const handleDropinError = (err) => {
    console.error('[PaymentPortal] Drop-in error:', err);
    const errorMessage = getErrorMessage(err);
    
    setError(errorMessage);
    setCurrentState(STATES.ERROR);
    onError?.(err);
  };

  // Handle retry
  const handleRetry = () => {
    setError(null);
    setCorrelationId(null);
    setValidationChecked(false);
    setCurrentState(STATES.VALIDATING);
  };

  // Render based on current state
  const renderContent = () => {
    switch (currentState) {
      case STATES.VALIDATING:
        return (
          <LoadingSpinner 
            size="lg" 
            message="Validating payment method request..." 
          />
        );

      case STATES.FETCHING_TOKEN:
        return (
          <LoadingSpinner 
            size="lg" 
            message="Loading payment method form..." 
          />
        );

      case STATES.PAYMENT_FORM:
        return (
          <div className="max-w-2xl mx-auto">
            {/* Salesforce Type: Case Details */}
            {requestType === 'Salesforce' && (
              <Card className="mb-6 shadow-md">
                <CardBody className="py-4">
                  <Typography variant="h6" className="text-fotl-purple mb-3">
                    Case Details
                  </Typography>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1">
                        Email
                      </Typography>
                      <Typography variant="paragraph" className="font-semibold">
                        {email}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1">
                        Case Number
                      </Typography>
                      <Typography variant="paragraph" className="font-semibold">
                        {caseNumber}
                      </Typography>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* PaymentMethod Type: Account Information */}
            {requestType === 'PaymentMethod' && customerName && (
              <Card className="mb-6 shadow-md">
                <CardBody className="py-4">
                  <Typography variant="h6" className="text-fotl-purple mb-3">
                    Payment Request Details
                  </Typography>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1">
                        Request ID
                      </Typography>
                      <Typography variant="paragraph" className="font-semibold">
                        {requestId}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1">
                        Customer Name
                      </Typography>
                      <Typography variant="paragraph" className="font-semibold">
                        {customerName}
                      </Typography>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
            
            {/* Payment Type: Invoice Details */}
            {requestType === 'Payment' && paymentData && (
              <Card className="mb-6 shadow-md">
                <CardBody className="py-4">
                  <Typography variant="h6" className="text-fotl-purple mb-3">
                    Invoice Details
                  </Typography>
                  
                  {/* Customer Name */}
                  <div className="mb-4">
                    <Typography variant="small" className="text-gray-600 mb-1">
                      Customer Name
                    </Typography>
                    <Typography variant="paragraph" className="font-semibold">
                      {customerName}
                    </Typography>
                  </div>
                  
                  {/* Addresses */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1 font-semibold">
                        Bill To
                      </Typography>
                      {paymentData.billTo ? (
                        <Typography variant="small" className="text-gray-800">
                          {paymentData.billTo.Street1}<br />
                          {paymentData.billTo.Street2 && <>{paymentData.billTo.Street2}<br /></>}
                          {paymentData.billTo.City}, {paymentData.billTo.State} {paymentData.billTo.Zip}
                        </Typography>
                      ) : (
                        <Typography variant="small" className="text-gray-400 italic">Not provided</Typography>
                      )}
                    </div>
                    <div>
                      <Typography variant="small" className="text-gray-600 mb-1 font-semibold">
                        Ship To
                      </Typography>
                      {paymentData.shipTo ? (
                        <Typography variant="small" className="text-gray-800">
                          {paymentData.shipTo.Street1}<br />
                          {paymentData.shipTo.Street2 && <>{paymentData.shipTo.Street2}<br /></>}
                          {paymentData.shipTo.City}, {paymentData.shipTo.State} {paymentData.shipTo.Zip}
                        </Typography>
                      ) : (
                        <Typography variant="small" className="text-gray-400 italic">Not provided</Typography>
                      )}
                    </div>
                  </div>
                  
                  {/* Line Items */}
                  <div className="mb-4">
                    <Typography variant="small" className="text-gray-600 mb-2 font-semibold">
                      Line Items
                    </Typography>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">Item</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-right">Price</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentData.lineItems.map((item, index) => (
                            <tr key={index} className="border-b">
                              <td className="px-3 py-2">{item.ItemNumber}</td>
                              <td className="px-3 py-2">{item.Description}</td>
                              <td className="px-3 py-2 text-right">${parseFloat(item.Price).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right">{item.Qty}</td>
                              <td className="px-3 py-2 text-right">${parseFloat(item.Total).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-end pt-3 border-t">
                    <div className="text-right">
                      <Typography variant="small" className="text-gray-600 mb-1">
                        Total Amount
                      </Typography>
                      <Typography variant="h5" className="font-bold text-fotl-purple">
                        ${parseFloat(paymentData.total).toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
            
            {/* Payment Form */}
            {requestType === 'PaymentMethod' && (
              <Typography variant="h6" className="text-gray-800 mb-4">
                Enter Payment Method
              </Typography>
            )}
            
            <BraintreeDropIn
              clientToken={clientToken}
              onPaymentMethodReceived={handlePaymentMethodReceived}
              onError={handleDropinError}
              buttonText={requestType === 'Payment' ? 'Submit Payment' : 'Save Payment Method'}
              title={requestType === 'Payment' ? '' : 'Enter Payment Method'}
            />
          </div>
        );

      case STATES.SUBMITTING:
        return (
          <LoadingSpinner 
            size="lg" 
            message="Saving payment method..." 
          />
        );

      case STATES.SUCCESS:
        return (
          <SuccessPage
            requestId={paymentResult?.requestId}
            paymentMethodId={paymentResult?.paymentMethodId}
            timestamp={paymentResult?.timestamp}
          />
        );

      case STATES.ERROR:
        return (
          <Card className="max-w-md mx-auto shadow-lg">
            <CardBody>
              <ErrorMessage
                message={error || 'An unexpected error occurred'}
                correlationId={correlationId}
              />

              <div className="flex gap-4 mt-6">
                <Button
                  className="flex-1 bg-fotl-purple hover:bg-fotl-purple-dark"
                  onClick={handleRetry}
                >
                  Try Again
                </Button>

                {onBack && (
                  <Button
                    variant="outlined"
                    className="flex-1 border-fotl-gray text-fotl-gray hover:bg-gray-50"
                    onClick={onBack}
                  >
                    Back
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="payment-portal">
      {renderContent()}
    </div>
  );
};

export default PaymentPortal;
